import React, { useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';

import { Bundle } from '@/ui/services/BundleService';
import { getUrlParamValue, updateUrlParam } from '@/ui/utils/client-url-helpers';
import { urlParameterCodes } from '@/constants/urlParams';
import { pushBundleViewItem } from '@/utils/bundle-helper';
import { useBundleVariantSelection } from '@/ui/hooks/productDetail/use-bundle-variant-selection';
import { BundleContextType } from './types/BundleContextType';

const BundleContext = React.createContext<BundleContextType | undefined>(undefined);

interface AvailableBundles {
  availableBundles: Bundle[] | null;
  availableSubscriptionBundles?: Bundle[] | null;
  availableUpgradeBundles?: Bundle[] | null;
}

/** The bundle the URL asks for (setId), from the list that matches the kind of chooser. */
function findBundleFromUrl(
  getParam: (name: string) => string | null,
  { availableBundles, availableSubscriptionBundles, availableUpgradeBundles }: AvailableBundles,
): Bundle | undefined {
  const bundleExtRefFromURL = getParam(urlParameterCodes.productDetail.bundleId);
  if (!bundleExtRefFromURL) {
    return undefined;
  }

  let bundles = availableBundles;
  if (getParam(urlParameterCodes.productDetail.subscription)) {
    bundles = availableSubscriptionBundles ?? null;
  } else if (getParam(urlParameterCodes.productDetail.upgrade)) {
    bundles = availableUpgradeBundles ?? null;
  }

  return bundles?.find((bundle) => bundle.documentExtRef === bundleExtRefFromURL);
}

export function BundleContextProvider({
  children,
  availableBundles,
  availableSubscriptionBundles,
  availableUpgradeBundles,
  hostProductId,
}: Readonly<{
  children: React.ReactNode;
  availableBundles: Bundle[] | null;
  availableSubscriptionBundles?: Bundle[] | null;
  availableUpgradeBundles?: Bundle[] | null;
  hostProductId?: string;
}>) {
  const searchParams = useSearchParams();
  // Selecting the URL's bundle in the first render avoids flashing the chooser without it, and
  // lets the variant selection seed that bundle in the same render.
  // useSearchParams (not window) keeps the server and the client render the same.
  const [selectedBundle, setSelectedBundle] = React.useState<Bundle | undefined>(() =>
    findBundleFromUrl((name) => searchParams?.get(name) ?? null, {
      availableBundles,
      availableSubscriptionBundles,
      availableUpgradeBundles,
    }),
  );
  const {
    selectedVariants,
    selectVariant: handleSelectVariant,
    resetForBundle: updateSelectedVariantsForBundle,
  } = useBundleVariantSelection(selectedBundle, { hostProductId });
  const lastTrackedBundleRef = React.useRef<string | null>(null);

  const trackBundleViewItem = React.useCallback((bundle: Bundle) => {
    if (lastTrackedBundleRef.current === bundle.documentExtRef) {
      return;
    }
    lastTrackedBundleRef.current = bundle.documentExtRef;
    pushBundleViewItem(bundle);
  }, []);

  useEffect(() => {
    // Keeps the preselection right when the bundle lists change, and tracks the view once.
    const preselectBundle = findBundleFromUrl(getUrlParamValue, {
      availableBundles,
      availableSubscriptionBundles,
      availableUpgradeBundles,
    });
    if (preselectBundle) {
      setSelectedBundle(preselectBundle);
      updateSelectedVariantsForBundle(preselectBundle);
      trackBundleViewItem(preselectBundle);
    }
  }, [
    availableBundles,
    availableSubscriptionBundles,
    availableUpgradeBundles,
    updateSelectedVariantsForBundle,
    trackBundleViewItem,
  ]);

  const selectBundle = React.useCallback(
    (bundle?: Bundle) => {
      updateUrlParam(urlParameterCodes.productDetail.bundleId, bundle?.documentExtRef);
      setSelectedBundle(bundle);
      if (bundle) {
        trackBundleViewItem(bundle);
      }
    },
    [trackBundleViewItem],
  );

  const value = useMemo<BundleContextType>(
    () => ({
      selectedBundle,
      selectBundle,
      selectedVariants,
      handleSelectVariant,
      updateSelectedVariantsForBundle,
      hostProductId,
    }),
    [
      selectedBundle,
      selectBundle,
      selectedVariants,
      handleSelectVariant,
      updateSelectedVariantsForBundle,
      hostProductId,
    ],
  );

  return <BundleContext.Provider value={value}>{children}</BundleContext.Provider>;
}

export const useBundleContext = (): BundleContextType => {
  const context = useContext(BundleContext);
  if (context === undefined) {
    throw new Error('useBundleContext must be used within a BundleContextProvider');
  }
  return context;
};

export default BundleContext;
